import classNames from 'classnames';
import { Pane } from '@campfire/pane';
import { Grid, Typography, List, ListItem, ListItemText, Box, Collapse, ListItemIcon } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import React, { useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useAdminConsoleRoutesMap } from './admin-console-routes-map';
import { SIDE_NAV_WIDTH } from '../AdminConsoleScreen';

const AdminConsoleSideNav = withRouter(({ match }: RouteComponentProps) => {
  const { routeGroups } = useAdminConsoleRoutesMap(match.path);

  // State to track which sections are expanded - System is open by default
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    System: true,
    General: false,
    'Activities & Rosters': false,
    Communications: false,
    Integrations: false,
  });

  const handleSectionToggle = (sectionName: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  return (
    <Grid
      item
      container
      style={{
        display: 'flex',
        minWidth: SIDE_NAV_WIDTH,
        maxWidth: SIDE_NAV_WIDTH,
        flexDirection: 'column',
      }}
    >
      <Grid item style={{ display: 'flex', width: '100%' }}>
        <Pane style={{ width: '100%' }}>
          <Box style={{ width: '100%', paddingBottom: '16px' }}>
            <List disablePadding>
              {routeGroups
                ?.filter((group) => group.routeOptions.some((option) => option.label !== ''))
                .map((group) => (
                  <React.Fragment key={group.heading}>
                    <ListItem
                      button
                      onClick={() => handleSectionToggle(group.heading)}
                      style={{ paddingLeft: '16px', paddingRight: '16px' }}
                    >
                      <ListItemIcon style={{ minWidth: '32px' }}>
                        <group.icon style={{ fontSize: '18px', color: '#666' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            style={{
                              whiteSpace: 'normal',
                              fontWeight: 600,
                              fontSize: '15px',
                              color: '#333',
                            }}
                            variant='body2'
                          >
                            {group.heading}
                          </Typography>
                        }
                      />
                      {expandedSections[group.heading] ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={expandedSections[group.heading]} timeout='auto' unmountOnExit>
                      <List component='div' disablePadding>
                        {group.routeOptions
                          .filter((option) => option.enabled !== false)
                          .map((option) => (
                            <AdminConsoleSideNavItems to={option.route} label={option.label} key={option.label} />
                          ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
            </List>
          </Box>
        </Pane>
      </Grid>
    </Grid>
  );
});

export { AdminConsoleSideNav };

interface AdminConsoleSideNavItemsProps extends RouteComponentProps {
  label: string;
  to: string;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      textDecoration: 'none',
      textDecorationColor: 'none',
      color: 'inherit',
      paddingLeft: 48, // Increased indentation for nested items
      paddingRight: 16,
      borderLeft: `4px solid transparent`,
      minHeight: '36px', // Smaller height
    },
    activeListItem: {
      backgroundColor: theme.color.grey.border,
      borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    activeListItemText: {
      fontWeight: 500,
    },
  })
);
const AdminConsoleSideNavItems = withRouter(({ location, history, to, label }: AdminConsoleSideNavItemsProps) => {
  const classes = useStyles();
  const active = location.pathname.includes(to.toLowerCase());
  return label ? (
    <ListItem
      disableGutters
      button
      onClick={() => history.push(to)}
      className={classNames(classes.listItem, { [classes.activeListItem]: active })}
    >
      <ListItemText
        style={{
          flex: label === 'Volunteer Profile' ? 'none' : '1 1 auto',
          paddingRight: label === 'Volunteer Profile' ? '7px' : '0',
        }}
        primary={
          <Typography
            variant='body2'
            style={{ fontSize: '14px' }}
            className={classNames({ [classes.activeListItemText]: active })}
          >
            {label}
          </Typography>
        }
      />
    </ListItem>
  ) : null;
});
